import type { OrderFragment, OrderLineFragment, PaymentMethodQuoteFragment, ProductOptionFragment, SearchProductResultFragment, SearchProductVariantResultFragment, SearchResultFragment, ShippingMethodQuoteFragment } from '#graphql-operations'
import { isPrizeCode } from '../utils/prizeCode'

/**
 * Customer-level figures resolved by the backend's `Order.analytics` field and
 * fetched by the app's own `orderAnalyticsByCode` query, so they are not part of
 * `OrderFragment`. Declared structurally and optional here so this shared layer
 * keeps type-checking against schemas that don't expose the field.
 *
 * The browser cannot compute any of this: Vendure's shop API returns an empty
 * `Customer.orders` for guest sessions, which is most of the checkouts.
 */
interface OrderAnalyticsFields {
  externalId?: string | null
  userId?: string | null
  ordersFromCustomer?: number | null
  lifetimeSpend?: number | null
  newCustomer?: boolean | null
  profit?: number | null
}

interface PurchaseOptions {
  analytics?: OrderAnalyticsFields | null
  /**
   * The delivery date the customer was actually shown during checkout. It is
   * computed from the storefront's dispatch-cutoff and per-market transit rules
   * (`useDeliveryEstimate`), which live in the app rather than this layer, so it
   * has to be passed in by the caller.
   */
  estimatedDeliveryDate?: Date | null
}

/**
 * How the payment method in an `add_payment_info` came to be selected. PostHog
 * only: GA4 has no use for it, but "which method do shoppers switch away from"
 * is only answerable with the row that was left and whether a human chose.
 */
interface PaymentSelection {
  /** The row the shopper sees, e.g. `stripe+apple_pay`, where the method code is just `stripe`. */
  option: string
  /** The row the previous `add_payment_info` of this checkout reported, if any. */
  previousOption?: string
  /** True when checkout picked the row for the shopper (the default or a late wallet row). */
  autoSelected: boolean
}

type AnalyticsEvent = { event: string, ecommerce?: Record<string, unknown> & { items?: unknown[] } } & Record<string, unknown>

/**
 * Mirrors a dataLayer event to PostHog. The `ecommerce` fields move to the top
 * level so they can be filtered on, and an event about a single product (a
 * view, a cart add) also gets that item spread onto it. `user_data` (contact
 * details and address) and `external_id` (the e-mail hash) are never sent: the
 * person is known by customer id instead, see `identifyPostHogCustomer`.
 */
function captureToPostHog(event: AnalyticsEvent, extra?: Record<string, unknown>) {
  const { event: name, ecommerce, user_data: _userData, external_id: _externalId, ...rest } = event
  const items = ecommerce?.items
  capturePostHogEvent(name, {
    ...rest,
    ...ecommerce,
    ...(items?.length === 1 ? items[0] as Record<string, unknown> : {}),
    ...extra,
  })
}

export function useAnalytics() {
  const { $gtm } = useNuxtApp()
  const route = useRoute()
  const activeOrderStore = useActiveOrderStore()

  // Read when the event fires, not when the composable is created: a component
  // that calls useAnalytics() before the active order has loaded would otherwise
  // report every later cart and checkout event without a currency.
  const currentCurrency = () => activeOrderStore.activeOrder.data?.currency

  // Abstraction for GA4 Ecommerce events
  const ecommerce = {
    addPaymentInfo: (paymentMethod?: PaymentMethodQuoteFragment, selection?: PaymentSelection) => {
      if (!paymentMethod || !activeOrderStore.activeOrder.data)
        return

      const event = {
        event: 'add_payment_info',
        ecommerce: {
          order_id: activeOrderStore.activeOrder.data.id,
          order_code: activeOrderStore.activeOrder.data.code,
          currency: currentCurrency(),
          value: formatPrice(activeOrderStore.activeOrder.data?.total),
          coupon: '',
          payment_type: paymentMethod.name,
          payment_method_id: paymentMethod.id,
          payment_method_code: paymentMethod.code,
          items: (activeOrderStore.activeOrder.data?.lines ?? []).map(line => ({
            item_brand: line.productVariant.product.id,
            item_id: line.productVariant.id,
            item_name: line.productVariant.product.name,
            item_variant: variantName(line.productVariant.options),
            discount: 0,
            price: formatPrice(line.productVariant.price),
            quantity: line.quantity,
          })),
        },
      }

      $gtm?.push?.({ ecommerce: null })
      $gtm?.push?.(event)
      captureToPostHog(event, selection && {
        payment_option: selection.option,
        previous_payment_option: selection.previousOption,
        auto_selected: selection.autoSelected,
      })
      const { ecommerce: { items: _, ...data } } = event
      pushFaroEvent(event.event, data, 'ecommerce')
    },
    addShippingInfo: (shippingMethod: ShippingMethodQuoteFragment) => {
      if (!activeOrderStore.activeOrder.data)
        return

      const event = {
        event: 'add_shipping_info',
        ecommerce: {
          order_code: activeOrderStore.activeOrder.data.code,
          order_id: activeOrderStore.activeOrder.data.id,
          currency: currentCurrency(),
          value: formatPrice(activeOrderStore.activeOrder.data?.total),
          coupon: '',
          shipping_tier: shippingMethod.name,
          shipping_method_id: shippingMethod.id,
          shipping_method_code: shippingMethod.code,
          items: (activeOrderStore.activeOrder.data?.lines ?? []).map(line => ({
            item_brand: line.productVariant.product.id,
            item_id: line.productVariant.id,
            item_name: line.productVariant.product.name,
            item_variant: variantName(line.productVariant.options),
            discount: 0,
            price: formatPrice(line.productVariant.price),
            quantity: line.quantity,
          })),
        },
      }

      $gtm?.push?.({ ecommerce: null })
      $gtm?.push?.(event)
      captureToPostHog(event)
      const { ecommerce: { items: _, ...data } } = event
      pushFaroEvent(event.event, data, 'ecommerce')
    },
    addToCart: (productVariantOrOrderLine: OrderLineFragment | SearchProductVariantResultFragment, quantity: number) => {
      if ('linePrice' in productVariantOrOrderLine) {
        if (!activeOrderStore.activeOrder.data)
          return

        const line = productVariantOrOrderLine

        const event = {
          event: 'add_to_cart',
          ecommerce: {
            order_id: activeOrderStore.activeOrder.data?.id,
            order_code: activeOrderStore.activeOrder.data?.code,
            currency: currentCurrency(),
            value: formatPrice(line.unitPrice * quantity), // quantity takes precedence over line.quantity because the original quantity is updated after the event is fired
            items: [{
              item_brand: line.productVariant.product.id,
              item_id: line.productVariant.id,
              item_name: line.productVariant.product.name,
              item_variant: variantName(line.productVariant.options),
              discount: 0,
              price: formatPrice(line.unitPrice),
              quantity, // quantity takes precedence over line.quantity because the original quantity is updated after the event is fired
            }],
          },
        }

        $gtm?.push?.({ ecommerce: null })
        $gtm?.push?.(event)
        captureToPostHog(event)
        const { ecommerce: { items: _, ...data } } = event
        pushFaroEvent(event.event, data, 'ecommerce')
      }
      else {
        const productVariant = productVariantOrOrderLine

        const { productId } = useSearchByProductId()

        const event = {
          event: 'add_to_cart',
          ecommerce: {
            order_id: activeOrderStore.activeOrder.data?.id,
            order_code: activeOrderStore.activeOrder.data?.code,
            currency: productVariant.currency,
            value: formatPrice(productVariant.price * quantity),
            items: [{
              // The caller's own product id first: off the product page (quick add, the
              // wishlist) the route carries none.
              item_brand: productVariant.productId ?? productId.value,
              item_id: productVariantOrOrderLine.id,
              item_name: productVariantOrOrderLine.name,
              item_variant: variantName(productVariantOrOrderLine.options),
              discount: 0,
              price: formatPrice(productVariant.price),
              quantity,
            }],
          },
        }

        $gtm?.push?.({ ecommerce: null })
        $gtm?.push?.(event)
        captureToPostHog(event)
        const { ecommerce: { items: _, ...data } } = event
        pushFaroEvent(event.event, data, 'ecommerce')
      }
    },
    addToWishlist: () => { /* ... */ },
    beginCheckout: () => {
      if (!activeOrderStore.activeOrder.data)
        return

      const event = {
        event: 'begin_checkout',
        ecommerce: {
          order_id: activeOrderStore.activeOrder.data.id,
          order_code: activeOrderStore.activeOrder.data.code,
          currency: currentCurrency(),
          value: formatPrice(activeOrderStore.activeOrder.data.total),
          coupon: '',
          items: activeOrderStore.activeOrder.data.lines.map(line => ({
            item_brand: line.productVariant.product.id,
            item_id: line.productVariant.id,
            item_name: line.productVariant.product.name,
            item_variant: variantName(line.productVariant.options),
            discount: 0,
            price: formatPrice(line.productVariant.price),
            quantity: line.quantity,
          })),
        },
      }

      $gtm?.push?.({ ecommerce: null })
      $gtm?.push?.(event)
      captureToPostHog(event)
      const { ecommerce: { items: _, ...data } } = event
      pushFaroEvent(event.event, data, 'ecommerce')
    },
    purchase: (order: OrderFragment, options: PurchaseOptions = {}) => {
      const { analytics } = options
      const event = {
        event: 'purchase',
        ecommerce: {
          order_id: order.id,
          order_code: order.code,
          transaction_id: order.code,
          // `subTotal`/`shipping` are aliased to the VAT-inclusive figures in the
          // Order fragment, so the net counterparts come from the `*Net` aliases.
          products: formatPrice(order.subTotal),
          products_without_vat: formatPrice(order.subTotalNet),
          shipping: formatPrice(order.shipping),
          shipping_without_vat: formatPrice(order.shippingNet),
          value: formatPrice(order.total),
          currency: order.currency,
          // A personal scratch-card prize code is left out: it discounts
          // nothing, it identifies one lead, and GA4's `coupon` is reported on.
          coupon: (() => {
            const codes = (order.couponCodes ?? []).filter(code => !isPrizeCode(code))
            return codes.length ? codes.join(', ') : undefined
          })(),
          // Money saved by promotions and coupons on this order, as a plain
          // positive number in the order's currency (no currency suffix).
          // Vendure returns discount amounts as negatives and already aggregates
          // line- and shipping-level promotions into `Order.discounts`, so a
          // single sum covers every discount applied.
          discount_value: formatPrice(Math.abs((order.discounts ?? []).reduce((sum, discount) => sum + discount.amount, 0))),
          items: order.lines.map(line => ({
            item_brand: line.productVariant.product.id,
            item_id: line.productVariant.id,
            item_name: line.productVariant.product.name,
            item_variant: variantName(line.productVariant.options),
            discount: formatPrice((line.unitPrice - line.discountedUnitPrice) * line.quantity),
            coupon: line.discounts?.find(d => d.type === 'PROMOTION' || d.type === 'DISTRIBUTED_ORDER_PROMOTION')?.description,
            price: formatPrice(line.productVariant.price),
            quantity: line.quantity,
          })),
        },
        // Customer-level attributes, kept outside `ecommerce` because they are
        // not GA4 ecommerce parameters and must survive the `{ ecommerce: null }`
        // reset push. Each is left `undefined` rather than defaulted when the
        // backend could not resolve it — a `false`/`0` there would read as a
        // known value and quietly poison audiences and value-based bidding.
        new_customer: analytics?.newCustomer ?? undefined,
        orders_from_customer: analytics?.ordersFromCustomer ?? undefined,
        // The backend returns minor units, so it goes through `formatPrice` like
        // every other money field here and lands in the same units as `value`.
        lifetime_spend: analytics?.lifetimeSpend != null ? formatPrice(analytics.lifetimeSpend) : undefined,
        // Gross margin on the goods, in the order's currency. Absent whenever any
        // line has no cost (or a zero cost) recorded in ABRA — the backend returns
        // null rather than a part-costed figure that would read as a real margin.
        // profit_from_order: analytics?.profit != null ? formatPrice(analytics.profit) : undefined,
        // SHA-256 of the e-mail: stable across devices and sessions, and the form
        // the Conversions APIs want. `user_id` is the opaque customer id instead,
        // so it survives the shopper changing their e-mail address.
        external_id: analytics?.externalId ?? undefined,
        user_id: analytics?.userId ?? undefined,
        // ISO 8601. The estimate is a calendar date anchored at UTC midnight, so
        // it carries a zero time-of-day rather than a spurious delivery hour.
        estimated_time_of_delivery: options.estimatedDeliveryDate?.toISOString(),
        user_data: {
          email_address: order.customer?.emailAddress,
          phone_number: order.customer?.phoneNumber,
          address: {
            first_name: order.customer?.firstName,
            last_name: order.customer?.lastName,
            street: order.shippingAddress?.streetLine1,
            city: order.shippingAddress?.city,
            region: order.shippingAddress?.province,
            country: order.shippingAddress?.country,
            postal_code: order.shippingAddress?.postalCode,
          },
        },
      }

      $gtm?.push?.({ ecommerce: null })
      $gtm?.push?.(event)
      // Before the capture, so the purchase and the anonymous browsing before it
      // land on the customer. Most orders are guest orders, which have no login
      // to identify on; the customer id still exists once the order is placed.
      identifyPostHogCustomer(analytics?.userId ?? order.customer?.id)
      captureToPostHog(event, {
        payment_method_code: order.payments?.at(-1)?.method ?? order.customFields?.intermediatePaymentMethod,
      })
      const { ecommerce: { items: _, ...data } } = event
      pushFaroEvent(event.event, data, 'ecommerce')
    },
    refund: () => { /* ... */ },
    removeFromCart: (line: OrderLineFragment, quantity: number) => {
      if (!activeOrderStore.activeOrder.data)
        return

      const event = {
        event: 'remove_from_cart',
        ecommerce: {
          order_id: activeOrderStore.activeOrder.data?.id,
          order_code: activeOrderStore.activeOrder.data?.code,
          currency: currentCurrency(),
          value: formatPrice(line.unitPrice * quantity), // quantity takes precedence over line.quantity because the original quantity is updated after the event is fired
          items: [{
            item_brand: line.productVariant.product.id,
            item_id: line.productVariant.id,
            item_name: line.productVariant.product.name,
            item_variant: variantName(line.productVariant.options),
            discount: 0,
            price: formatPrice(line.unitPrice),
            quantity, // quantity takes precedence over line.quantity because the original quantity is updated after the event is fired
          }],
        },
      }

      $gtm?.push?.({ ecommerce: null })
      $gtm?.push?.(event)
      captureToPostHog(event)
      const { ecommerce: { items: _, ...data } } = event
      pushFaroEvent(event.event, data, 'ecommerce')
    },
    selectItem: (item: SearchResultFragment) => {
      const event = {
        event: 'select_item',
        item_list_id: route.name,
        item_list_name: route.meta.title ?? route.name,
        ecommerce: {
          currency: currentCurrency(),
          items: [{
            item_id: item.id,
            item_name: item.name,
            discount: 0,
            price: formatPrice(item.price),
            quantity: 1,
          }],
        },
      }

      $gtm?.push?.({ ecommerce: null })
      $gtm?.push?.(event)
      captureToPostHog(event)
      const { item_list_id, item_list_name, ecommerce: { items: _, ...data } } = event
      pushFaroEvent(event.event, { item_list_id, item_list_name, ...data }, 'ecommerce')
    },
    selectPromotion: () => { /* ... */ },
    viewCart: () => {
      if (!activeOrderStore.activeOrder.data)
        return

      const event = {
        event: 'view_cart',
        ecommerce: {
          order_id: activeOrderStore.activeOrder.data.id,
          order_code: activeOrderStore.activeOrder.data.code,
          currency: currentCurrency(),
          value: formatPrice(activeOrderStore.activeOrder.data?.subTotal),
          items: (activeOrderStore.activeOrder.data?.lines ?? []).map(line => ({
            item_brand: line.productVariant.product.id,
            item_id: line.productVariant.id,
            item_name: line.productVariant.product.name,
            item_variant: variantName(line.productVariant.options),
            discount: 0,
            price: formatPrice(line.productVariant.price),
            quantity: line.quantity,
          })),
        },
      }

      $gtm?.push?.({ ecommerce: null })
      $gtm?.push?.(event)
      captureToPostHog(event)
      const { ecommerce: { items: _, ...data } } = event
      pushFaroEvent(event.event, data, 'ecommerce')
    },
    viewItem: (productOrVariant: SearchProductResultFragment | SearchProductVariantResultFragment, productVariantId?: string | number) => {
      $gtm?.push?.({ ecommerce: null })

      if ('variants' in productOrVariant) {
        const product = productOrVariant
        const variant = productVariantId ? product.variants?.find(v => v.id === productVariantId) : product.variants?.[0]

        if (!variant)
          return

        const event = {
          event: 'view_item',
          ecommerce: {
            currency: product.currency,
            items: [{
              item_brand: product.id,
              item_id: product.id,
              item_name: product.name,
              item_variant_id: productVariantId ? variant.id : null,
              quantity: 1,
              price: formatPrice(variant.price),
            }],
          },
        }

        $gtm?.push?.(event)
        captureToPostHog(event)
        // A view_item carries exactly one item, so it is spread flat rather
        // than dropped like the multi-item events above, so nothing is lost.
        const { items: [item], ...rest } = event.ecommerce
        pushFaroEvent(event.event, { ...rest, ...item }, 'ecommerce')
      }
      else {
        const variant = productOrVariant

        const event = {
          event: 'view_item',
          ecommerce: {
            currency: variant.currency,
            items: [{
              item_brand: variant.productId,
              item_id: variant.id,
              item_name: variant.name,
              quantity: 1,
              price: formatPrice(variant.price),
            }],
          },
        }

        $gtm?.push?.(event)
        captureToPostHog(event)
        const { items: [item], ...rest } = event.ecommerce
        pushFaroEvent(event.event, { ...rest, ...item }, 'ecommerce')
      }
    },
    viewItemList: (items?: SearchResultFragment[]) => {
      if (items?.length) {
        const event = {
          event: 'view_item_list',
          ecommerce: {
            currency: currentCurrency(),
            item_list_id: route.name,
            item_list_name: document.title ?? route.name,
            items: items.map((item, index) => ({
              index,
              item_id: item.id,
              item_name: item.name,
              price: formatPrice(item.price),
              quantity: 1,
            })),
          },
        }

        $gtm?.push?.({ ecommerce: null })
        $gtm?.push?.(event)
        // The list itself is a whole collection grid, so it is replaced by its
        // length here: the same treatment every other multi-item event gets.
        const { items: listItems, ...data } = event.ecommerce
        pushFaroEvent(event.event, { ...data, items_count: listItems.length }, 'ecommerce')
        // Fired on every collection page, filter and pagination, so PostHog gets
        // the count too rather than a copy of the grid on each one.
        captureToPostHog({ event: event.event, ecommerce: data }, { items_count: listItems.length })
      }
    },
    viewPromotion: () => { /* ... */ },
    // common
    login: (method?: string) => {
      const event = {
        event: 'login',
        method,
      }
      $gtm?.push?.(event)
      captureToPostHog(event)
      pushFaroEvent(event.event, { method }, 'ecommerce')
    },
    signUp: (method?: string) => {
      const event = {
        event: 'sign_up',
        method,
      }
      $gtm?.push?.(event)
      captureToPostHog(event)
      pushFaroEvent(event.event, { method }, 'ecommerce')
    },
    search: (searchQuery: string) => {
      const event = {
        event: 'search',
        search_term: searchQuery,
      }

      $gtm?.push?.(event)
      captureToPostHog(event)
      pushFaroEvent(event.event, { search_term: searchQuery }, 'ecommerce')
    },
  }

  function pushLead(name: string, params: Record<string, string | number | boolean>) {
    $gtm?.push?.({ event: name, ...params })
    capturePostHogEvent(name, params)
    pushFaroEvent(name, params, 'ecommerce')
  }

  // Lead capture (scratch to win). Never an e-mail address, phone number, token or
  // code: there is no consent-management platform in front of the dataLayer.
  const lead = {
    scratchView: (p: { cardId: string, trigger: 'TIMED' | 'EXIT_INTENT' | 'MANUAL', startStep: string, audience: 'anonymous' | 'account' }) =>
      pushLead('scratch_view', { card_id: p.cardId, trigger: p.trigger, start_step: p.startStep, audience: p.audience }),
    scratchRevealed: (p: { cardId: string, method: 'scratch' | 'button' }) =>
      pushLead('scratch_revealed', { card_id: p.cardId, method: p.method }),
    generateLead: (p: { cardId: string, codeSent: boolean }) =>
      pushLead('generate_lead', { method: 'scratch_email', lead_source: 'scratch_card', card_id: p.cardId, code_sent: p.codeSent }),
    scratchPhoneSubmitted: (p: { cardId: string, country: string, skipEmail: boolean, codeSent: boolean }) =>
      pushLead('scratch_phone_submitted', { card_id: p.cardId, country: p.country, skip_email: p.skipEmail, code_sent: p.codeSent }),
    scratchComplete: (p: { cardId: string, phoneProvided: boolean }) =>
      pushLead('scratch_complete', { card_id: p.cardId, phone_provided: p.phoneProvided }),
    scratchPrizeApplied: (p: { cardId: string, giftState: 'pending' | 'added' | 'waiting_condition', via: 'auto' | 'swap' }) =>
      pushLead('scratch_prize_applied', { card_id: p.cardId, gift_state: p.giftState, via: p.via }),
  }

  return {
    ecommerce,
    lead,
  }
}

const formatPriceCache = new Map<number, number>()
function formatPrice(n?: { min: number, max: number } | { value: number } | number) {
  if (n == null)
    return 0

  const num = typeof n === 'number' ? n : 'min' in n ? n.min : n.value

  if (formatPriceCache.has(num))
    return formatPriceCache.get(num)!

  const formatted = num / 100
  formatPriceCache.set(num, formatted)

  return formatted
}

function variantName(options?: ProductOptionFragment[]) {
  // This is also used by vendure when creating the product variant name
  // https://github.com/vendure-ecommerce/vendure/blob/master/packages/admin-ui/src/lib/catalog/src/providers/product-detail/product-detail.service.ts#L120
  return (options ?? []).map(o => o.name).join(' ').trim()
}
